import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import React, { useMemo } from 'react';
import TitleField from '../TitleField';
import { Card, Typography, Chip, Table, TableBody, TableRow, TableCell, Divider, Box, Grid } from '@material-ui/core';

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(2),
    width: '100%',
  },

  section: {
    marginTop: theme.spacing(2),
    pageBreakInside: 'avoid',
  },

  label: {
    fontWeight: 500,
    color: '#666',
    fontSize: 13,
  },

  successChip: {
    backgroundColor: '#e6f4ea',
    color: '#1e7e34',
    fontWeight: 600,
  },

  failChip: {
    backgroundColor: '#fdecea',
    color: '#c62828',
    fontWeight: 600,
  },

  gridImage: {
    width: '100%',
    height: 180,
    objectFit: 'contain',
    borderRadius: 6,
    border: '1px solid #ddd',
    background: '#fafafa',
  },
}));

// 🔥 FORMATTER
const formatFieldValue = (fieldType, value) => {
  if (!value) return '-';

  switch (fieldType) {
    case 'Date of birth':
    case 'Date of issue': {
      const parsed = new Date(value);
      return isNaN(parsed.getTime())
        ? value
        : parsed.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          });
    }

    case 'Sex':
      return value === 'M' ? 'Male' : value === 'F' ? 'Female' : value;

    case 'Address':
      return value.split('\n').map((line, i) => (
        <span key={i}>
          {line}
          <br />
        </span>
      ));

    default:
      return value;
  }
};

const RegtankDocumentDownload = ({ documents = {}, docField = {}, docImage = {}, className }) => {
  const classes = useStyles();

  const docInfo = documents?.documentInfo || {};
  const idvV1Info = documents?.idvV1Info || {};
  const extensions = idvV1Info?.documentExtensions || [];
  const fields = idvV1Info?.ocrV2Info?.documentFields || [];

  const hasDocField = Array.isArray(docField) && docField.length > 0;

  const displayFields = useMemo(() => {
    if (!hasDocField) {
      return fields.map((f) => ({
        fieldType: f.fieldType,
        label: f.fieldType,
        value: f.visualZone || f.barcode || f.mrz || '-',
        valid: null,
      }));
    }

    return docField.map((df) => {
      const match = fields.find((f) => f.fieldType === df.fieldType);
      return {
        fieldType: df.fieldType,
        label: df.fieldType,
        value: match?.visualZone || match?.barcode || match?.mrz || '-',
        valid: df.valid,
      };
    });
  }, [docField, fields]);

  const errorFields = displayFields.filter((f) => f.valid === 'ERROR');

  const getExt = (key) => extensions.find((e) => e.key === key)?.value;

  const renderImageQuality = (title, data) => {
    if (!data) return null;

    return (
      <Box className={classes.section}>
        <Typography style={{ fontWeight: 600, marginBottom: 8 }}>📷 Image Quality ({title})</Typography>

        {Object.entries(data).map(([key, value]) => {
          if (!value || value === 'WAS_NOT_DONE') return null;

          const isError = value === 'ERROR';

          return (
            <Box key={key} display="flex" justifyContent="space-between" alignItems="center" borderBottom="1px solid #eee" py={1}>
              <Typography style={{ color: '#666' }}>{key.replace(/([A-Z])/g, ' $1')}</Typography>

              <Chip label={value} size="small" className={isError ? classes.failChip : classes.successChip} />
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Card className={clsx(classes.card, className)}>
      <TitleField>ID Photos</TitleField>

      {/* 🔥 2×2 GRID IMAGE LAYOUT */}
      <Box className={classes.section}>
        <Grid container spacing={2}>
          {docInfo.frontDocumentUrl && (
            <Grid item xs={6}>
              <Typography style={{ marginBottom: 6 }}>Front</Typography>
              <img src={docInfo.frontDocumentUrl} className={classes.gridImage} />
            </Grid>
          )}

          {docInfo.backDocumentUrl && (
            <Grid item xs={6}>
              <Typography style={{ marginBottom: 6 }}>Back</Typography>
              <img src={docInfo.backDocumentUrl} className={classes.gridImage} />
            </Grid>
          )}

          {getExt('Portrait') && (
            <Grid item xs={6}>
              <Typography style={{ marginBottom: 6 }}>Portrait</Typography>
              <img src={getExt('Portrait')} className={classes.gridImage} />
            </Grid>
          )}

          {getExt('Barcode') && (
            <Grid item xs={6}>
              <Typography style={{ marginBottom: 6 }}>Barcode</Typography>
              <img src={getExt('Barcode')} className={classes.gridImage} />
            </Grid>
          )}
        </Grid>
      </Box>

      {/* 🔥 IMAGE QUALITY */}
      {renderImageQuality('Front', docImage?.front)}
      {renderImageQuality('Back', docImage?.back)}

      <Divider style={{ margin: '16px 0' }} />
      <TitleField>Verification Summary</TitleField>

      {/* 🔴 ISSUES */}
      {errorFields.length > 0 && (
        <Box className={classes.section}>
          <Typography style={{ fontWeight: 600, marginBottom: 8 }}>⚠️ Issues Found</Typography>

          <Table>
            <TableBody>
              {errorFields.map((f, i) => (
                <TableRow key={i} style={{ background: '#fff5f5' }}>
                  <TableCell className={classes.label}>{f.label}</TableCell>
                  <TableCell>{formatFieldValue(f.fieldType, f.value)}</TableCell>
                  <TableCell align="right">
                    <Chip label="ERROR" size="small" className={classes.failChip} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* 🔥 ALL FIELDS */}
      <Box className={classes.section}>
        {displayFields.map((f, i) => (
          <Box key={i} display="flex" justifyContent="space-between" alignItems="center" borderBottom="1px solid #eee" py={1}>
            <Typography style={{ color: '#666' }}>{f.label}</Typography>

            <Typography component="div" style={{ fontWeight: 500 }}>
              {formatFieldValue(f.fieldType, f.value)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Card>
  );
};

export default RegtankDocumentDownload;
